import { KAPLAYCtx } from "kaplay";
import { PHYSICS, SPEED_LINES } from "./constants";
import type { Camera, SpeedLineParticle } from "./types";

export function createSpeedLineParticle(
  playerX: number,
  playerY: number,
  playerVx: number,
  playerVy: number,
  verticalOffset = 0,
): SpeedLineParticle {
  const angle = Math.random() * Math.PI * 2;
  const distance = Math.random() * SPEED_LINES.SPAWN_RADIUS;
  const x = playerX + Math.cos(angle) * distance;
  const y = playerY + verticalOffset + Math.sin(angle) * distance;

  const particleVx =
    -playerVx * PHYSICS.PTM_RATIO * SPEED_LINES.VELOCITY_FACTOR +
    (Math.random() - 0.5) * 15;
  const particleVy =
    -playerVy * PHYSICS.PTM_RATIO * SPEED_LINES.VELOCITY_FACTOR +
    (Math.random() - 0.5) * 15;

  const initialSpeed = Math.hypot(particleVx, particleVy);

  return {
    x,
    y,
    vx: particleVx,
    vy: particleVy,
    age: 0,
    maxAge: SPEED_LINES.PARTICLE_LIFETIME,
    alpha: 1.0,
    initialSpeed,
  };
}

export function updateSpeedLineParticles(
  particles: SpeedLineParticle[],
  dt: number,
  playerX: number,
  playerY: number,
  playerVx: number,
  playerVy: number,
  isDiving: boolean,
  lastSpawnTime: number,
  verticalOffset = 0,
): number {
  let newLastSpawnTime = lastSpawnTime;

  for (let i = particles.length - 1; i >= 0; i--) {
    const particle = particles[i];

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    particle.x += particle.vx * dt;
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    particle.y += particle.vy * dt;

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    particle.vx *= SPEED_LINES.DECELERATION_FACTOR;
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    particle.vy *= SPEED_LINES.DECELERATION_FACTOR;

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    particle.age += dt;
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    particle.alpha = 1.0 - particle.age / particle.maxAge;

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (particle.age >= particle.maxAge) {
      particles.splice(i, 1);
    }
  }

  if (
    isDiving &&
    playerVy > SPEED_LINES.DOWNWARD_SPEED_THRESHOLD &&
    particles.length < SPEED_LINES.MAX_PARTICLES
  ) {
    const currentTime = Date.now() / 1000;
    if (currentTime - lastSpawnTime > SPEED_LINES.SPAWN_RATE) {
      particles.push(
        createSpeedLineParticle(
          playerX,
          playerY,
          playerVx,
          playerVy,
          verticalOffset,
        ),
      );
      newLastSpawnTime = currentTime;
    }
  }

  return newLastSpawnTime;
}

export function renderSpeedLineParticles(
  k: KAPLAYCtx,
  particles: SpeedLineParticle[],
  camera: Camera,
): void {
  const currentCameraPos = k.getCamPos();
  const viewportWidth = k.width() / camera.scale;
  const viewportHeight = k.height() / camera.scale;

  for (const particle of particles) {
    if (
      particle.x < currentCameraPos.x - viewportWidth / 2 - 200 ||
      particle.x > currentCameraPos.x + viewportWidth / 2 + 200 ||
      particle.y < currentCameraPos.y - viewportHeight / 2 - 200 ||
      particle.y > currentCameraPos.y + viewportHeight / 2 + 200
    ) {
      continue;
    }

    const currentSpeed = Math.hypot(particle.vx, particle.vy);
    const speedRatio = currentSpeed / particle.initialSpeed;

    const ageRatio = 1.0 - particle.age / particle.maxAge;
    const trailLength =
      SPEED_LINES.MIN_LINE_LENGTH +
      (SPEED_LINES.MAX_LINE_LENGTH - SPEED_LINES.MIN_LINE_LENGTH) *
        speedRatio *
        ageRatio;

    if (currentSpeed > 0.1) {
      const directionX = particle.vx / currentSpeed;
      const directionY = particle.vy / currentSpeed;

      const lineEndX = particle.x + directionX * trailLength;
      const lineEndY = particle.y + directionY * trailLength;

      k.drawLine({
        p1: k.vec2(particle.x, particle.y),
        p2: k.vec2(lineEndX, lineEndY),
        width: SPEED_LINES.LINE_WIDTH / camera.scale,
        color: k.rgb(255, 255, 255),
        opacity: particle.alpha * 0.8,
      });
    }
  }
}
